import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, useMe } from '../lib/auth';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onLogin = pathname === '/login';
  // Hide nav links on login and when no token is present. Brand stays visible.
  const showNav = !onLogin && isAuthenticated();
  // Don't block render on the me query; the admin link can pop in once it
  // resolves. The query is gated on isAuthenticated() inside useMe.
  const me = useMe();
  const isAdmin = me.data?.role === 'ADMIN';

  return (
    <div className="min-h-full">
      <header className="border-b border-border bg-surface">
        <nav className="mx-auto flex max-w-5xl items-center gap-6 px-6 py-4">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <img src="/logo.png" alt="Teraseya Fly" className="h-7 w-auto" />
          </Link>
          {showNav && (
            <Link
              to="/health"
              className="text-sm text-text-muted hover:text-text"
              activeProps={{ className: 'text-text font-medium' }}
            >
              Health
            </Link>
          )}
          {showNav && isAdmin && (
            <Link
              to="/admin/design-system"
              className="text-sm text-text-muted hover:text-text"
              activeProps={{ className: 'text-text font-medium' }}
            >
              {t('admin.designSystem.navLabel')}
            </Link>
          )}
        </nav>
      </header>
      <main className={onLogin ? '' : 'mx-auto max-w-5xl px-6 py-8'}>
        <Outlet />
      </main>
    </div>
  );
}
